import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { ModalService } from '../../../base/shared/modal/modal.service';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { ToolbarModule } from 'primeng/toolbar';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { ToastModule } from 'primeng/toast';
import {
    PrivacyPolicyDTO,
    CreatePrivacyPolicyDTO,
    UpdatePrivacyPolicyDTO,
    FilterDTO,
    FilterOperator,
    SortDirection
} from '../../../base/models/models.swagger.generated';

import { PagedQueryModel } from '../../../base/shared/table/paged-query-model';
import { FilterMap } from '../../../base/shared/table/filter-map';
import { FilterMetadata } from 'primeng/api';
import { Subscription } from 'rxjs';


import { PrivacyPolicyService } from './privacy-policy.service';
import { PrivacyPolicyFormType } from './privacy-policy-edit-form';
import { EditorModule } from 'primeng/editor';
import { TagModule } from 'primeng/tag';
import { CheckboxModule } from 'primeng/checkbox';
import { MessageModule } from 'primeng/message';

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'app-Privacy-Policy',
    standalone: true,
    imports: [
        ReactiveFormsModule,
        FormsModule,
        TableModule,
        ToolbarModule,
        ButtonModule,
        DialogModule,
        InputTextModule,
        ToastModule,
        TranslateModule,
        CommonModule,
        EditorModule,
        TagModule,
        CheckboxModule,
        MessageModule,
    ],
    templateUrl: './privacy-policy.component.html',
    styleUrls: ['./privacy-policy.component.scss'],
    providers: [PrivacyPolicyService]
})
export class PrivacyPolicyComponent implements OnInit, OnDestroy {
    public data: PrivacyPolicyDTO[] = [];
    public totalNoOfRecords = 0;
    public privacyPolicy: PrivacyPolicyDTO;
    public itemDialog = false;
    public pagedQuery: PagedQueryModel = PagedQueryModel.getDefault();
    public form: FormGroup<PrivacyPolicyFormType>;
    public globalFilters = ['id', 'name', 'PolicyContent', 'Version', 'ForceReacceptance', 'CreatedOn', 'ModifiedOn'];
    public columnFilters: { [key: string]: FilterMetadata | FilterMetadata[] } = {};
    private _subscription: Subscription = new Subscription();
    private searchText = '';
    public isEditMode = false;
    public submitted = false;
    public isReadOnly = false;
    public isRowEditMode = false;
    public selectedRow: PrivacyPolicyDTO | null = null;

    constructor(
        private PrivacyPolicyService: PrivacyPolicyService,
        private router: Router,
        private fb: FormBuilder,
        private translateService: TranslateService,
        private modalService: ModalService
    ) { }

    ngOnInit() {
        this.pagedQuery.sortBy = 'version';
        this.pagedQuery.sortDirection = SortDirection.Descending;
        this.loadData();
    }

    private loadData() {
        this._subscription.add(
            this.PrivacyPolicyService.getData(this.pagedQuery).subscribe((data) => {
                this.data = data.items;
                this.data.sort((a, b) => {
                    const v1 = this.parseVersion(a.version || '0.0.0');
                    const v2 = this.parseVersion(b.version || '0.0.0');
                    for (let i = 0; i < Math.max(v1.length, v2.length); i++) {
                        const n1 = v1[i] || 0;
                        const n2 = v2[i] || 0;
                        if (n1 !== n2) {
                            return n2 - n1;
                        }
                    }
                    return 0;
                });

                this.totalNoOfRecords = data.totalNumberOfItems;
                this.privacyPolicy = null;
                this.itemDialog = false;
            })
        );
    }

    public versionSort(event: any) {
        this.data.sort((a: any, b: any) => {
            const v1 = this.parseVersion(a.version || '0.0.0');
            const v2 = this.parseVersion(b.version || '0.0.0');

            for (let i = 0; i < Math.max(v1.length, v2.length); i++) {
                const n1 = v1[i] || 0;
                const n2 = v2[i] || 0;
                if (n1 !== n2) {
                    return (n1 - n2) * event.order;
                }
            }
            return 0;
        });
    }

    private parseVersion(version: string): number[] {
        return version.split('.').map(x => parseInt(x, 10));
    }

    private getFilters(): FilterDTO[] {
        const filters: FilterDTO[] = [];
        const addFilter = (field: string, operator: FilterOperator, value: any) => {
            filters.push(new FilterDTO({ field, operator, value, isComplex: false }));
        };
        if (this.searchText) {
            addFilter('name', FilterOperator.Contains, this.searchText);
        }
        Object.entries(this.columnFilters).forEach(([key, filter]) => {
            if (Array.isArray(filter)) {
                filter.forEach(f => f.value && addFilter(key, FilterMap.getFilterOperator(f.matchMode), f.value));
            } else if (filter?.value) {
                addFilter(key, FilterMap.getFilterOperator(filter.matchMode), filter.value);
            }
        });
        return filters;
    }

    public onFilter(event: any) {
        this.columnFilters = event.filters;
        this.pagedQuery.filters = this.getFilters();
        this.loadData();
    }

    public openNew() {
        this.isRowEditMode = false;
        const latest = this.data && this.data.length > 0 ? this.data[0] : null;


        if (latest) {
            this.privacyPolicy = new PrivacyPolicyDTO({ ...latest, id: undefined });
        } else {
            this.privacyPolicy = new PrivacyPolicyDTO();
        }

        this.initForm();
        this.isReadOnly = false;
        this.itemDialog = true;
    }

    public amendPolicy() {
        this.isReadOnly = false;
        this.form.enable();
        this.form.markAsPristine();
    }

    private incrementVersion(version: string): string {
        const parts = this.parseVersion(version);
        parts[parts.length - 1] = (parts[parts.length - 1] || 0) + 1;
        return parts.join('.');
    }

    public editItem(item: PrivacyPolicyDTO) {
        this.isRowEditMode = true;
        this.selectedRow = item;
        this.privacyPolicy = new PrivacyPolicyDTO(item);
        this.initForm();
        this.isReadOnly = true;
        this.itemDialog = true;
        this.form.markAsPristine();
    }

    public hideDialog() {
        this.itemDialog = false;
    }

    public savePrivacyPolicy() {
        if (this.form.invalid || !this.form.dirty) {
            return;
        }

        const privacypolicyData = this.form.value;

        if (privacypolicyData.Id) {
            const updateDto = new UpdatePrivacyPolicyDTO({
                id: privacypolicyData.Id,
                name: privacypolicyData.Name,
                policyContent: privacypolicyData.PolicyContent,
                version: privacypolicyData.Version,
                forceReacceptance: privacypolicyData.ForceReacceptance
            });
            this._subscription.add(
                this.PrivacyPolicyService.updateRecord(updateDto).subscribe(() => {
                    this.loadData();
                })
            );
        } else {
            this._subscription.add(
                this.PrivacyPolicyService.addRecord(
                    privacypolicyData as CreatePrivacyPolicyDTO
                ).subscribe(() => {
                    this.loadData();
                })
            );
        }
    }

    public onPageChange(event: any) {
        this.pagedQuery.skip = event.first;
        this.pagedQuery.take = event.rows;
        this.loadData();
    }

    public onSort(event: any) {
        if (event.field === 'version') {
            this.versionSort(event);
        } else {
            this.data.sort((a, b) => {
                const value1 = a[event.field];
                const value2 = b[event.field];
                return (value1 > value2 ? 1 : value1 < value2 ? -1 : 0) * event.order;
            });
        }
    }

    private initForm() {
        const { id, name, version, forceReacceptance, createdOn, modifiedOn, policyContent } = this.privacyPolicy || {};
        this.form = this.fb.group<PrivacyPolicyFormType>({
            Id: this.fb.control<number>(id),
            Name: this.fb.control<string>(name || '', Validators.required),
            ForceReacceptance: this.fb.control<boolean>(forceReacceptance ?? true, Validators.required),
            Version: this.fb.control<string>(version || '', Validators.required),
            CreatedOn: this.fb.control<Date>(
                createdOn ? createdOn.toJSDate() : new Date(),
                Validators.required
            ),
            ModifiedOn: this.fb.control<Date>(
                modifiedOn ? modifiedOn.toJSDate() : new Date(),
                Validators.required
            ),
            PolicyContent: this.fb.control<string>(policyContent || '', Validators.required)
        });
    }

    public onSearchTextChanged(searchText: string) {
        this.searchText = searchText;
        this.pagedQuery.filters = this.getFilters();
        this.loadData();
    }

    ngOnDestroy(): void {
        this._subscription?.unsubscribe();
    }
}
